import { noop, uniqBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { PAGE_SIZE } from '~/common/constants/page';
import { UserDepartmentSelect as EdsUserDepartmentSelect } from '~/eds/src/components/Select/subcomponents/UserDepartmentSelect';
import { Option } from '~/eds/src/types/Option';
import { PilotId } from '~/eds/src/types/PilotId';
import { User } from '~/eds/src/types/User';
import { EntityType } from '~/enums/EntityType';
import { WorkflowTicketRoleType } from '~/enums/WorkflowTicketRoleType';
import { withClient } from '~/hocs/withClient';
import { useDebouncedCallback } from '~/hooks/useDebouncedCallback';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { DEPRECATED_Client } from '~/types/Client';
import {
  DepartmentV2,
  NormalizedDepartment,
  NormalizedDepartments,
} from '~/types/Department';
import { normalizeDepartmentTree } from '~/utils/departments';
import { getUserName } from '~/utils/user';

type DepartmentValue = {
  id: number;
  type: EntityType.Department;
  role: null;
  name?: string;
};

type UserValue = {
  id: number;
  type: EntityType.User;
  role: null;
  name?: string;
};

type RoleValue = {
  id: null;
  type: EntityType.User;
  role: WorkflowTicketRoleType;
  name?: string;
};

type UserDepartmentSelectValue = DepartmentValue | RoleValue | UserValue;

type Role = WorkflowTicketRoleType;

type OptionsEntityTypes =
  | EntityType.User
  | EntityType.Department
  | EntityType.Role;

interface Props {
  value: Array<UserDepartmentSelectValue>;
  roles?: Option<Role>[];
  enablePortal?: boolean;
  error?: string;
  onChange: (values: Array<UserDepartmentSelectValue> | null) => void;
  // connected
  client: DEPRECATED_Client;
}

const initialUserSearchParams = {
  query: '',
  pageSize: PAGE_SIZE,
};

const UserDepartmentSelect = ({
  client,
  enablePortal,
  error,
  roles = [],
  value,
  onChange = noop,
}: Props) => {
  const intl = useIntl();

  const { data: clientDepartmentsTree, isFetching: isFetchingDepartmentsTree } =
    api.endpoints.getClientDepartmentsTree.useQuery(client.id);
  const departments: NormalizedDepartments = useMemo(() => {
    const departmentTree = clientDepartmentsTree ?? client.departmentTree;
    // use client's preloaded departmentTree until the updated one is queried
    // this way, preloaded values are shown right away and there is no render shift as the queried departments are loaded in
    return normalizeDepartmentTree(
      departmentTree ?? [],
    ) as NormalizedDepartments;
  }, [clientDepartmentsTree, client.departmentTree]);

  const [findUsers, usersResult] =
    api.endpoints.findUsersByNameOrEmail.useLazyQuery();
  const { error: fetchUsersError, isFetching: isFetchingUsers } = usersResult;
  const [findDepartments, departmentsResult] =
    api.endpoints.findDepartmentsByName.useLazyQuery();
  const { error: fetchDepartmentsError, isFetching: isFetchingDepartments } =
    departmentsResult;
  const [
    resolveUsers,
    { data: resolvedUsersData, isFetching: isFetchingResolvedUsers },
  ] = api.endpoints.resolveUsers.useLazyQuery();
  const initialDepartmentSearchParams = useMemo(
    () =>
      client.id
        ? { clientId: client.id, pageSize: PAGE_SIZE, query: '' }
        : null,
    [client],
  );

  const debouncedFindUsers = useDebouncedCallback(
    findUsers,
    initialUserSearchParams,
  );
  const debouncedFindDepartments = useDebouncedCallback(
    findDepartments,
    initialDepartmentSearchParams,
  );

  const fetchedUsers = usersResult?.data?.results || [];
  const fetchedDepartments = departmentsResult?.data?.results || [];

  const onChangeValues = (values: Array<PilotId> | null) => {
    const parsedUserDepartments = (values || []).map((value) =>
      transfromValuesToData(value, allUsers, allDepartments, intl),
    );
    onChange(parsedUserDepartments);
  };

  // Remove deleted items from values and transform into MultiSelect ids
  const preloadedValues = useMemo(
    () =>
      value
        .filter((item) => {
          if (item.role) {
            return roles.some((role) => role.value === item.role);
          }

          if (item.type === EntityType.Department) {
            return item.id && !!departments[item.id];
          }

          return true;
        })
        .map(transformDataToValues),
    [value, departments],
  );
  const [preloadedDepartments, setPreloadedDepartments] = useState<
    Array<DepartmentV2>
  >([]);
  const [preloadedUsers, setPreloadedUsers] = useState<Array<User>>([]);

  useEffect(() => {
    const departmentOptions: Array<DepartmentV2> = [];
    const usersToResolve: Array<number> = [];
    value.forEach((val: UserDepartmentSelectValue) => {
      if (val.type === EntityType.User) {
        if (val.id) {
          usersToResolve.push(val.id);
        }
      } else {
        if (val.id && departments[val.id]) {
          departmentOptions.push(
            transfromDepartmentsToOptions(departments[val.id], departments),
          );
        }
      }
    });
    usersToResolve.length &&
      resolveUsers({
        ids: usersToResolve,
        params: { includeDeleted: true, clientId: client.id },
      });
    setPreloadedDepartments(departmentOptions);
  }, [value, departments]);

  useEffect(() => {
    resolvedUsersData && setPreloadedUsers(Object.values(resolvedUsersData));
  }, [resolvedUsersData]);

  const allUsers = useMemo(
    () =>
      uniqBy([...fetchedUsers, ...preloadedUsers], 'id').map((val) =>
        formatID(val, EntityType.User),
      ) as Array<User>,
    [fetchedUsers, preloadedUsers],
  );
  const allDepartments = useMemo(
    () =>
      uniqBy([...fetchedDepartments, ...preloadedDepartments], 'id').map(
        (val) => formatID(val, EntityType.Department),
      ) as Array<DepartmentV2>,
    [fetchedDepartments, preloadedDepartments],
  );

  const parsedRoles = useMemo(
    () =>
      roles.map((role) => ({
        ...role,
        value: `${EntityType.Role}-${role.value}`,
      })) as Option<string>[],
    [roles],
  );

  return (
    <EdsUserDepartmentSelect
      name="User Department Multi Select"
      placeholder={intl.formatMessage({
        id: 'UserDepartmentSelect.placeholder',
        defaultMessage: 'Specify one or more departments or users...',
        description: 'Placeholder text for user department select input',
      })}
      isMulti
      isLoading={
        isFetchingUsers ||
        isFetchingDepartments ||
        isFetchingResolvedUsers ||
        isFetchingDepartmentsTree ||
        !client.id
      }
      users={allUsers}
      roles={parsedRoles}
      departments={allDepartments}
      value={preloadedValues}
      onChange={onChangeValues}
      enablePortal={enablePortal}
      error={
        coerceRtkqError(fetchUsersError) ||
        coerceRtkqError(fetchDepartmentsError) ||
        error
      }
      width="100%"
      onSearch={(query: string) => {
        debouncedFindUsers({ query, pageSize: PAGE_SIZE });
        debouncedFindDepartments({
          query,
          pageSize: PAGE_SIZE,
          clientId: client.id,
        });
      }}
    />
  );
};

export const formatID = (value: DepartmentV2 | User, type: EntityType) => {
  return {
    ...value,
    id: `${type}-${value.id}`,
  };
};

export const transformDataToValues = (
  data: UserDepartmentSelectValue,
): string => {
  if (data.role) {
    return `${EntityType.Role}-${data.role}`;
  }
  return `${data.type}-${data.id}`;
};

export const transfromValuesToData = (
  combinedValue: PilotId,
  allUsers: User[],
  allDepartments: DepartmentV2[],
  intl: IntlShape,
): UserDepartmentSelectValue => {
  const [type, value] = String(combinedValue).split('-', 2) as [
    OptionsEntityTypes,
    WorkflowTicketRoleType | string,
  ];
  switch (type) {
    case EntityType.Role:
      return {
        id: null,
        type: EntityType.User,
        role: value as WorkflowTicketRoleType,
        name: `${EntityType.Role}-${value}`,
      };
    case EntityType.Department:
      return {
        id: Number(value),
        type: type,
        role: null,
        name: allDepartments.find((dept) => dept.id === combinedValue)?.name,
      };
    case EntityType.User:
      return {
        id: Number(value),
        type: type,
        role: null,
        name: getUserName(
          allUsers.find((user) => user.id === combinedValue),
          intl,
        ),
      };
    default:
      return { id: null, role: null, type: null, name: null } as any;
  }
};

export const transfromDepartmentsToOptions = (
  value: NormalizedDepartment,
  departments: NormalizedDepartments,
): DepartmentV2 => {
  return {
    id: value.id,
    name: value.name,
    parent: value.position,
    path: value.parentIds.map((id) => departments[id].name),
    members: [],
    members_count: 0,
  };
};

export default withClient(UserDepartmentSelect);
